// @flow
import React from 'react';
import classnames from 'classnames';
import styled, { type StyledComponent } from 'styled-components';
import { I18n } from '@lingui/react';

import type { Untranslated } from '@rover/shared/js/types';
import { FONT_FAMILY, DSToken, FontSize, Spacing } from '@rover/react-lib/styles';
import { SENSITIVE_DATA_CLASS } from '@rover/react-lib/constants/sensitiveData.constants';
import LabelAndErrorFormGroup, {
  type Props as LabelAndErrorFormGroupProps,
} from '@rover/react-lib/components/utils/LabelAndErrorFormGroup';

const PLACEHOLDER_VALUE = 'placeholder';

const renderPlaceholder = placeholder => {
  if (placeholder) {
    return (
      <option disabled value={PLACEHOLDER_VALUE}>
        {placeholder}
      </option>
    );
  }
  return null;
};

export const StyledSelect: StyledComponent<{}> = styled.select`
  background-color: ${DSToken.BACKGROUND_COLOR_PRIMARY};
  background-image: none;
  border-radius: ${DSToken.BORDER_RADIUS};
  border: 1px solid ${DSToken.BORDER_COLOR_PRIMARY};
  color: ${DSToken.TEXT_COLOR_SECONDARY};
  display: block;
  font-family: ${FONT_FAMILY};
  font-size: ${FontSize.M};
  height: 40px;
  line-height: 1.6;
  padding: ${Spacing.S} ${Spacing.M} ${Spacing.S} ${Spacing.XS};
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  width: 100%;
  box-shadow: none;

  &:disabled {
    border: 1px solid ${DSToken.INTERACTIVE_BORDER_COLOR_DISABLED};
    background-color: ${DSToken.INTERACTIVE_BACKGROUND_COLOR_DISABLED};
    color: ${DSToken.TEXT_COLOR_TERTIARY};
    cursor: not-allowed;
    opacity: 1;
  }
`;

export type Props<T: string | number> = {
  value: T,
  // Flow's built-in Array type expect the exact type so the optional
  // `disabled` field clashes with consumers who don't pass it in at all
  options: Array<
    { value: T, title: Untranslated, disabled: boolean } | { value: T, title: Untranslated }
  >,
  onChange: T => void,
  placeholder: string,
  className?: string,
  sensitive?: boolean,
};

const Select = <T: string | number>({
  value,
  options,
  placeholder,
  onChange,
  className,
  sensitive,
  ...other
}: Props<T>) => (
  <I18n>
    {({ i18n }) => (
      <StyledSelect
        className={classnames(className, {
          [SENSITIVE_DATA_CLASS]: sensitive,
        })}
        value={value || PLACEHOLDER_VALUE}
        onChange={e => onChange(e.target.value)}
        {...other}
      >
        {renderPlaceholder(placeholder)}
        {options.map(option => (
          <option
            value={option.value}
            key={option.value}
            disabled={typeof option.disabled === 'boolean' ? option.disabled : false}
          >
            {i18n._(option.title)}
          </option>
        ))}
      </StyledSelect>
    )}
  </I18n>
);

Select.defaultProps = {
  value: '',
  placeholder: '',
  className: '',
  sensitive: false,
};

export { Select as UnwrappedSelect };

export default <T: string | number>(
  props: LabelAndErrorFormGroupProps & $Diff<Props<T>, { placeholder: string }>
) => <LabelAndErrorFormGroup {...props} InputComponent={Select} />;



// WEBPACK FOOTER //
// ../react-lib/src/components/formFields/Select/Select.jsx